import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  InputBase,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  MusicNote as MusicNoteIcon,
  Album as AlbumIcon,
  PlaylistPlay as PlaylistIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import searchService from '../../services/searchService';
import { getImageUrl } from '../../constants/routes';

const SearchBar = ({ 
  placeholder = "Search for music, artists, playlists...",
  onSearch, 
  showSuggestions = true,
  maxSuggestions = 8,
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);
  const [searchStats, setSearchStats] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Load search stats on component mount
  useEffect(() => {
    const loadSearchStats = async () => {
      try {
        const stats = await searchService.getSearchStats();
        setSearchStats(stats);
      } catch (error) {
        console.error('Failed to load search stats:', error);
      }
    };
    loadSearchStats();
  }, []);

  // Debounced search for suggestions
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const suggestionsData = await searchService.getSuggestions(query, maxSuggestions);
          setSuggestions(suggestionsData);
          setShowSuggestionsList(true);
        } catch (error) {
          console.error('Failed to fetch suggestions:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestionsList(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, maxSuggestions]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowSuggestionsList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setShowSuggestionsList(false);
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      // Default behavior: navigate to search results page
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    } else if (event.key === 'Escape') {
      setShowSuggestionsList(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.name);
    setShowSuggestionsList(false);
    
    // Navigate to the appropriate page based on suggestion type
    switch (suggestion.type) {
      case 'act':
        // Use slug if available, fallback to id for backward compatibility
        const actIdentifier = suggestion.slug || suggestion.id;
        navigate(`/act-page/${actIdentifier}`);
        break;
      case 'track':
        // Use slug if available, fallback to id for backward compatibility
        const trackIdentifier = suggestion.slug || suggestion.id;
        navigate(`/single-page/${trackIdentifier}`);
        break;
      case 'album':
        // Use slug if available, fallback to id for backward compatibility
        const albumIdentifier = suggestion.slug || suggestion.id;
        navigate(`/album-page/${albumIdentifier}`);
        break;
      case 'playlist':
        navigate(`/playlist/${suggestion.id}`);
        break;
      case 'creator':
        navigate(`/creator/${suggestion.id}/profile`);
        break;
      case 'label':
        navigate(`/labels/${suggestion.id}`);
        break;
      default:
        handleSearch(suggestion.name);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestionsList(false);
    inputRef.current?.focus();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'act':
        return <PersonIcon fontSize="small" />;
      case 'track':
        return <MusicNoteIcon fontSize="small" />;
      case 'album':
        return <AlbumIcon fontSize="small" />;
      case 'playlist':
        return <PlaylistIcon fontSize="small" />;
      case 'creator':
        return <PersonIcon fontSize="small" />;
      case 'label':
        return <BusinessIcon fontSize="small" />;
      default:
        return <SearchIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'act':
        return 'primary';
      case 'track':
        return 'secondary';
      case 'album':
        return 'success';
      case 'playlist':
        return 'warning';
      case 'creator':
        return 'info';
      case 'label':
        return 'default';
      default:
        return 'default';
    }
  };

  const getSizeStyles = () => {
    // Mobile-specific sizing
    if (isMobile) {
      return {
        height: 36,
        fontSize: '14px',
        padding: '4px 8px',
        minWidth: '120px', // Ensure minimum width for mobile
      };
    }
    
    switch (size) {
      case 'small':
        return {
          height: 32,
          fontSize: '12px',
          padding: '4px 8px',
        };
      case 'large':
        return {
          height: 48,
          fontSize: '16px',
          padding: '8px 16px',
        };
      default: // medium
        return {
          height: 40,
          fontSize: '14px',
          padding: '6px 12px',
        };
    }
  };

  // Get mobile-optimized placeholder
  const getMobilePlaceholder = () => {
    if (isMobile) {
      return ""; // No placeholder text on mobile
    }
    return placeholder;
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      maxWidth: isMobile ? '200px' : 400,
      minWidth: isMobile ? '120px' : 'auto'
    }}>
      <Paper 
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: isMobile ? '18px' : '20px',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
          '&:focus-within': {
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.main,
          },
          ...getSizeStyles()
        }}
      >
        <InputBase
          ref={inputRef}
          sx={{ 
            flex: 1, 
            padding: isMobile ? theme.spacing(0.5, 1) : theme.spacing(1, 2),
            fontSize: getSizeStyles().fontSize,
            '& input': {
              padding: 0,
            },
            // Make placeholder text lighter for oasis theme
            '& .MuiInputBase-input::placeholder': {
              opacity: theme.palette.background.default === '#8B7355' ? 0.5 : 0.7, // Lighter for oasis theme
              color: theme.palette.text.secondary,
            },
          }}
          placeholder={getMobilePlaceholder()}
          value={query}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestionsList(true);
            }
          }}
          inputProps={{
            'aria-label': 'search',
            style: { fontSize: getSizeStyles().fontSize }
          }}
        />
        
        {query && (
          <IconButton
            size="small"
            onClick={clearSearch}
            sx={{ padding: 0.5, marginRight: 0.5 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
        
        <IconButton
          onClick={() => handleSearch()}
          disabled={!query.trim()}
          size="small"
          sx={{ 
            padding: isMobile ? theme.spacing(0.5) : theme.spacing(1), 
            marginRight: isMobile ? theme.spacing(0.25) : theme.spacing(0.5) 
          }}
        >
          <SearchIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
      </Paper>

      {showSuggestions && showSuggestionsList && (
        <Paper 
          ref={suggestionsRef} 
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: theme.spacing(0.5),
            maxHeight: 400,
            overflow: 'auto',
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.spacing(1),
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: theme.spacing(2) }}>
              <CircularProgress size={20} />
            </Box>
          ) : suggestions.length > 0 ? (
            <>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <React.Fragment key={`${suggestion.type}-${suggestion.id}`}>
                    <ListItem
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        padding: theme.spacing(1, 2),
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{ width: 32, height: 32, backgroundColor: theme.palette.grey[300] }}
                          src={suggestion.image ? getImageUrl(suggestion.image) : undefined}
                        >
                          {getTypeIcon(suggestion.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={suggestion.name}
                        secondary={
                          suggestion.act_name || 
                          suggestion.creator_name || 
                          suggestion.display_name ||
                          searchService.getContentTypeDisplayName(suggestion.type)
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '14px',
                            fontWeight: 500,
                          },
                          '& .MuiListItemText-secondary': {
                            fontSize: '12px',
                            color: theme.palette.text.secondary,
                          },
                        }}
                      />
                      <Chip
                        label={searchService.getContentTypeDisplayName(suggestion.type)}
                        size="small"
                        color={getTypeColor(suggestion.type)}
                        sx={{ height: 20, fontSize: '10px', marginLeft: theme.spacing(1) }}
                      />
                    </ListItem>
                    {index < suggestions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              
              {searchStats && (
                <Box sx={{
                  padding: theme.spacing(1, 2),
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}>
                  <Typography variant="caption" color="text.secondary">
                    {searchStats.total_acts} artists • {searchStats.total_tracks} tracks • {searchStats.total_albums} albums • {searchStats.total_playlists} playlists
                  </Typography>
                </Box>
              )}
            </>
          ) : query.length >= 2 ? (
            <Box sx={{ padding: theme.spacing(2), textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2" color="text.secondary">
                No results found for "{query}"
              </Typography>
            </Box>
          ) : null}
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;